import { useEffect, useRef, useState } from 'react';
import { Copy, Check, X } from 'lucide-react';
import { ApiError } from '../api/client';
import type { ApiClient } from '../api/client';
import { createHost, type CreatedHost } from '../api/hosts';
import { Button } from './Button';
import { Card } from './Card';
import { Field } from './Field';
import { Input } from './Input';
import { useFocusTrap } from '../hooks/use-focus-trap';

interface Props {
  api: ApiClient;
  onClose: () => void;
  onCreated: () => void;
}

type State =
  | { kind: 'idle' }
  | { kind: 'creating' }
  | { kind: 'done'; host: CreatedHost; copied: boolean }
  | { kind: 'error'; message: string };

export function CreateHostDialog({ api, onClose, onCreated }: Props) {
  const [name, setName] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const inputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  useFocusTrap(cardRef, true);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit(): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) {
      setState({ kind: 'error', message: 'Name is required.' });
      return;
    }
    setState({ kind: 'creating' });
    try {
      const host = await createHost(api, trimmed);
      onCreated();
      setState({ kind: 'done', host, copied: false });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setState({ kind: 'error', message: 'Invalid name (1-100 characters).' });
      } else {
        setState({ kind: 'error', message: 'Could not create host. Please try again.' });
      }
    }
  }

  async function copyToken(): Promise<void> {
    if (state.kind !== 'done') return;
    try { await navigator.clipboard.writeText(state.host.hostToken); setState({ ...state, copied: true }); }
    catch { /* clipboard blocked; user can select+copy manually */ }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-host-dialog-title"
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card ref={cardRef} variant="elevated" className="w-full max-w-md p-6">
        <div className="flex items-start justify-between">
          <h2
            id="create-host-dialog-title"
            className="font-[var(--font-serif)] text-xl font-bold italic text-text-primary"
          >
            {state.kind === 'done' ? 'Host created' : 'New host'}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-[var(--radius-sm)] p-1 text-text-muted hover:text-text-primary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {state.kind !== 'done' && (
          <>
            <p className="mt-2 text-sm text-text-secondary">
              Give this host a name so you can identify it in the list.
            </p>
            <form
              className="mt-5 space-y-4"
              onSubmit={(e) => { e.preventDefault(); void submit(); }}
            >
              <Field label="Name" error={state.kind === 'error' ? state.message : null}>
                <Input
                  ref={inputRef}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="desk-1"
                  maxLength={100}
                  disabled={state.kind === 'creating'}
                  invalid={state.kind === 'error'}
                />
              </Field>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
                <Button type="submit" loading={state.kind === 'creating'}>
                  Create host
                </Button>
              </div>
            </form>
          </>
        )}

        {state.kind === 'done' && (
          <>
            <p className="mt-2 text-sm text-text-secondary">
              Copy the token below — <strong className="text-text-primary">it will not be shown again</strong>.
              Paste it into the host agent&apos;s <code className="font-mono text-xs text-accent">RELAY_HOST_TOKEN</code> environment variable.
            </p>
            <div className="mt-4 space-y-3">
              <Field label="Host name"><span className="block font-mono text-sm text-text-primary">{state.host.name}</span></Field>
              <Field label="Host ID"><span className="block font-mono text-sm text-text-primary">{state.host.id}</span></Field>
              <Field label="One-time token">
                <div className="flex items-start gap-2">
                  <code className="flex-1 break-all rounded-[var(--radius-md)] border border-[color:var(--color-accent-edge)] bg-bg-base px-3 py-2 font-mono text-xs text-accent">
                    {state.host.hostToken}
                  </code>
                  <Button variant="secondary" size="sm" type="button" onClick={() => { void copyToken(); }} aria-label="Copy token">
                    {state.copied
                      ? <><Check className="h-3.5 w-3.5 text-accent" /> Copied</>
                      : <><Copy className="h-3.5 w-3.5" /> Copy</>
                    }
                  </Button>
                </div>
              </Field>
            </div>
            <div className="mt-6 flex justify-end">
              <Button onClick={onClose}>Done</Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
